import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import PageHeader from 'components/Header/Page';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import LoaderComponent from 'components/Loader';
import QueryBuilder from './QueryBuilder';
import { workspaceGetEnv, getonlyWorkspaces, statusOverviewData, fetchSectorList } from 'service/service';
import PageWrapper from 'components/Common/PageWraper';
import FiltersBar from './FilterBar';
import {
    QueryRule,
    FieldOption,
    useAdbFilters,
    commonFetchSectors,
    syncEnvWithOptions,
    handleExportExcel,
    ENV_OPTIONS_DEFAULT
} from 'utils/adb-common-util';

export interface ComputeFlatTableBaseProps {
    serviceFunc: any;
    pageTitle: string;
    breadcrumbLabel: string;
    breadcrumbPath: string;
    tableType: string;
    defaultColumnNames: string[];
    getColumns: () => any[];
    columnToApiMap?: Record<string, string>;
    preserveFilterKey: string;
    getInitialQueryFilters: (navState: any) => QueryRule[];
    mapQueryRuleToApi: (rule: QueryRule, workspaceOptions: FieldOption[]) => { field: string; value: string } | null;
    transformRecords?: (records: any[]) => any[];
    emptyStateMessage?: string;
    isJob?: boolean;
    rootActiveTab?: number;
    middleBreadcrumb?: { label: string; path: string; state?: any };
    statusOptions?: FieldOption[];
    showQueryBuilder?: boolean;
}

const ComputeFlatTableBase: React.FC<ComputeFlatTableBaseProps> = ({
    serviceFunc,
    pageTitle,
    breadcrumbLabel,
    breadcrumbPath,
    tableType,
    defaultColumnNames,
    getColumns,
    columnToApiMap = {},
    preserveFilterKey,
    getInitialQueryFilters,
    mapQueryRuleToApi,
    transformRecords = (r) => r,
    emptyStateMessage = 'No data found for the selected filters.',
    isJob = true,
    rootActiveTab = 0,
    middleBreadcrumb,
    statusOptions,
    showQueryBuilder = true
}) => {
    const {
        startDate, setStartDate, endDate, setEndDate,
        env, setEnv, sector, setSector, sectorName, setSectorName,
        location, setLocation, selectedDateInString, setSelectedDateInString,
        apiDateRangeKey, updateHistoryState, handleDateSelect, envRef, initialEnvAppliedRef, navigationState
    } = useAdbFilters(preserveFilterKey);

    const [loader, setLoader] = useState(false);
    const [tableRecords, setTableRecords] = useState<any[]>([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
    const [sortingInfo, setSortingInfo] = useState<{ sortBy: string; sortDir: 'asc' | 'desc' | '' }>({ sortBy: '', sortDir: '' });
    const lastFetchParamsRef = useRef<string>('');

    const [queryBuilderFilters, setQueryBuilderFilters] = useState<QueryRule[]>([]);
    const [queryBuilderKey, setQueryBuilderKey] = useState(0);
    const [workspaceOptions, setWorkspaceOptions] = useState<FieldOption[]>([{ label: 'All', value: 'all' }]);
    const [jobstatusOverview, setJobstatusOverview] = useState<{ _id: string; totalJobCount: number }[]>([]);
    const [envOptionsState, setEnvOptionsState] = useState<{ label: string; value: string }[]>(ENV_OPTIONS_DEFAULT);
    const [sectorList, setSectorList] = useState<string[]>([]);

    useEffect(() => { commonFetchSectors(fetchSectorList, setSectorList); }, []);

    useEffect(() => {
        const initial = getInitialQueryFilters(navigationState);
        if (initial.length > 0) {
            setQueryBuilderFilters(initial);
            setQueryBuilderKey(prev => prev + 1);
        }
    }, [navigationState, getInitialQueryFilters]);

    useEffect(() => {
        const getEnv = async (loc: string) => {
            try {
                const res = await workspaceGetEnv(loc);
                const options = [{ label: 'ALL', value: 'both' }, ...(res?.data || [])];
                setEnvOptionsState(options);
                syncEnvWithOptions(options, initialEnvAppliedRef, envRef, navigationState?.env, setEnv);
            } catch (err) { console.error(err); }
        };
        getEnv(location);
    }, [location, navigationState?.env]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const resp = await getonlyWorkspaces({ startDate, endDate, env: env === 'both' ? '' : env, sector: sector === 'all' ? '' : sector, location: location === 'ALL' ? '' : location, exportPage: 1, exportPageSize: 1000, filters: [], selectedDateInString });
                if (resp?.success && Array.isArray(resp.data)) {
                    const wsMap = new Map();
                    resp.data.forEach((w: any) => { if (w.workspace_id && w.workspace_name) wsMap.set(w.workspace_id, w.workspace_name); });
                    const sorted = Array.from(wsMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
                    setWorkspaceOptions([{ label: 'All', value: 'all' }, ...sorted.map(([id, name]) => ({ label: name, value: id, workspaceName: name, workspaceId: id }))]);
                }
            } catch (e) { console.error(e); }

            try {
                const statusResp = await statusOverviewData(startDate, endDate, env, sector, location, selectedDateInString);
                if (statusResp?.success) setJobstatusOverview(statusResp.data || []);
            } catch (e) { console.error(e); }
        };
        fetchOptions();
    }, [startDate, endDate, env, sector, location, selectedDateInString]);

    const buildApiFilters = useCallback(() => {
        const filters: any[] = [];
        queryBuilderFilters.forEach(rule => {
            const mapped = mapQueryRuleToApi(rule, workspaceOptions);
            if (mapped) filters.push({ operator: 'AND', ...mapped });
        });
        return filters.length > 0 ? filters : undefined;
    }, [queryBuilderFilters, workspaceOptions, mapQueryRuleToApi]);

    const fetchData = useCallback(async (isExport = false) => {
        if (!isExport) setLoader(true);
        try {
            const filters = buildApiFilters();
            let sortField, sortOrder;
            if (sortingInfo.sortBy && sortingInfo.sortDir) {
                sortField = columnToApiMap[sortingInfo.sortBy] || sortingInfo.sortBy;
                sortOrder = sortingInfo.sortDir;
            }

            const fetchParams = JSON.stringify({
                startDate, endDate, env, sector, location,
                dateRange: apiDateRangeKey, page, pageSize, sortField, sortOrder, filters, isExport
            });

            if (!isExport && lastFetchParamsRef.current === fetchParams) {
                setLoader(false);
                return;
            }
            if (!isExport) lastFetchParamsRef.current = fetchParams;

            const resp = await serviceFunc({
                startDate,
                endDate,
                env,
                sector,
                location,
                selectedDateInString: apiDateRangeKey,
                page,
                pageSize,
                sortField,
                sortOrder,
                filters,
                exportType: isExport ? 'excel' : undefined
            });
            if (isExport) {
                handleExportExcel(resp);
            } else if (resp?.success && resp.data?.docs) {
                setTableRecords(transformRecords(resp.data.docs));
                const totalElements = resp.data.pagination?.totalRecords || resp.data.docs.length;
                setPaginationData({
                    totalElements,
                    totalPages: resp.data.pagination?.totalPages || Math.ceil(totalElements / pageSize)
                });
            } else {
                setTableRecords([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } catch (e) {
            console.error(e);
            if (!isExport) {
                setTableRecords([]);
                setPaginationData({ totalElements: 0, totalPages: 0 });
            }
        } finally { if (!isExport) setLoader(false); }
    }, [startDate, endDate, env, sector, location, apiDateRangeKey, page, pageSize, sortingInfo, buildApiFilters, serviceFunc, columnToApiMap, transformRecords]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleRunQuery = (rules: QueryRule[]) => {
        setQueryBuilderFilters(rules.filter(r => r.value && r.value.toString().toLowerCase() !== 'all'));
        setPage(1);
    };

    const renderTable = () => {
        if (loader) return <LoaderComponent pt={20} />;
        if (tableRecords.length === 0) {
            return (
                <Box sx={{ textAlign: 'center', py: 10, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0', mx: 2 }}>
                    <Box sx={{ fontSize: '1.2rem', color: '#666', mb: 1 }}>No Data Available</Box>
                    <Box sx={{ fontSize: '0.9rem', color: '#999' }}>{emptyStateMessage}</Box>
                    <Box sx={{ fontSize: '0.85rem', color: '#999', mt: 2 }}>Date Range: {startDate} to {endDate}</Box>
                    <Box sx={{ fontSize: '0.85rem', color: '#999' }}>Location: {location} | Environment: {env} | Sector: {sector}</Box>
                </Box>
            );
        }
        return (
            <Box sx={{ px: 2 }}>
                <CustomizedDataGrid
                    title={`${pageTitle} (${paginationData.totalElements.toLocaleString()} records)`}
                    tableColumns={getColumns()} tableData={tableRecords} tableType={tableType}
                    defaultColumnNames={defaultColumnNames} page={page} pageSize={pageSize}
                    paginationData={paginationData} listLength={tableRecords.length}
                    handlepageSizeChange={(e: any) => { setPageSize(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(_: any, v: number) => setPage(v)}
                    isDateRangeFilter={false} isDisabled={false} isFetching={loader}
                    showExport={true} exportTableData={() => fetchData(true)}
                    onSorting={(info: any) => setSortingInfo(info?.sortBy && info.sortDir ? info : { sortBy: '', sortDir: '' })}
                    currSorting={sortingInfo}
                />
            </Box>
        );
    };

    return (
        <PageWrapper>
            <PageHeader
                title={[
                    {
                        text: navigationState?.pageHeader || breadcrumbLabel,
                        link: navigationState?.breadcrumbPath || breadcrumbPath,
                        state: { startDate, endDate, env, sector: sectorName, location, selectedDateInString, activeTab: rootActiveTab }
                    },
                    ...(middleBreadcrumb ? [{
                        text: middleBreadcrumb.label,
                        link: middleBreadcrumb.path,
                        state: { startDate, endDate, env, sector: sectorName, location, selectedDateInString, ...middleBreadcrumb.state }
                    }] : []),
                    {
                        text: `${pageTitle}${(startDate && endDate) ? ` (${startDate === endDate ? startDate : `${startDate} to ${endDate}`})` : ''}`
                    }
                ]}
                isPepwise2Breadcrumb={true}
            />

            <FiltersBar
                location={location} setLocation={setLocation}
                env={env} setEnv={setEnv} envOptions={envOptionsState} setEnvOptions={setEnvOptionsState}
                startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate}
                handleDateSelect={handleDateSelect} selectedDateInString={selectedDateInString} setSelectedDateInString={setSelectedDateInString}
                sectorName={sectorName}
                setSectorName={(v: any) => {
                    const resolved = typeof v === 'function' ? v(sectorName) : v;
                    setSectorName(resolved); setSector(resolved);
                }}
                updateHistoryState={updateHistoryState} sectorOptionsList={sectorList}
                useHeaderFilters
            />

            {showQueryBuilder && (
                <Box sx={{ px: 2, mb: 2 }}>
                    <QueryBuilder
                        key={queryBuilderKey} title="Filter Options" sectorOptions={workspaceOptions}
                        environmentOptions={statusOptions || jobstatusOverview?.map(({ _id }) => ({ label: _id, value: _id })) || []}
                        resultStateOptions={statusOptions || jobstatusOverview?.map(({ _id }) => ({ label: _id, value: _id })) || []}
                        locationOptions={[]} teamFlagOptions={[]} onRunQuery={handleRunQuery}
                        showQueryPreference={false} initialFilters={queryBuilderFilters} isJob={isJob}
                    />
                </Box>
            )}

            {renderTable()}
        </PageWrapper>
    );
};

export default ComputeFlatTableBase;
